import React, { useState } from "react";
import { msgid, ngettext, t } from "ttag";

import type {
    GridPromoPlacements,
    ServerCmsTypesProductSummary as ProductSummary,
} from "@reactivated";

import { Themed } from "../../styles/themes";
import { interleavePromos } from "../CategoryContentBlock/utils";
import { Clickable } from "../Clickables";
import { GridPromoCard } from "../GridPromoCard";
import { ProductCard } from "../ProductCard";
import { skeletonProduct } from "../ProductCard/skeletonData";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { Svg } from "../Svg";
import { Tooltip } from "../Tooltip";

import styles from "./index.module.scss";

type RichTextValue = React.ComponentProps<typeof RichTextStreamBlock>["value"];

interface FavoritesContentProps {
    products: ProductSummary[] | null;
    isLoading: boolean;
    hasError: boolean;
    /** SKUs used for the skeleton grid while loading. */
    skus: string[];
    /** Fully-qualified share URL; null hides the share/email buttons. */
    shareUrl: string | null;
    /** Editorial error copy; null hides the error state block. */
    errorMessage: string | null;
    /** Editorial footer richtext; null hides the footer. */
    footerContent: RichTextValue;
    /** Whether product cards render the favorite heart. Defaults to true. */
    showFavoriteButton?: boolean;
    /** Grid promo placements interleaved into the product grid when loaded. */
    gridPromos?: GridPromoPlacements;
}

export const FavoritesContent = ({
    products,
    isLoading,
    hasError,
    skus,
    shareUrl,
    errorMessage,
    footerContent,
    showFavoriteButton = true,
    gridPromos,
}: FavoritesContentProps) => {
    const [copied, setCopied] = useState(false);

    const productCount = products?.length ?? skus.length;

    const shareLabel = t`Click to copy the page link.`;
    const shareLabelSuccess = t`Copied!`;
    const emailLabel = t`Send via email.`;
    const printLabel = t`Print this page.`;
    const emailSubject = ngettext(
        msgid`An SKS appliance has been shared with you - open to review!`,
        `SKS appliances have been shared with you - open to review!`,
        productCount,
    );

    const mailtoLink = shareUrl
        ? `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(shareUrl)}`
        : null;

    /* v8 ignore next -- @preserve */
    const printPage = () => window.print();

    const copyShareLink = () => {
        /* v8 ignore next -- @preserve */
        if (!shareUrl) return;
        navigator.clipboard
            .writeText(shareUrl)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(console.error);
    };

    return (
        <Themed
            theme="offWhite"
            tag="section"
            className={styles.favoritesState}
        >
            <header className={styles.gridHeader}>
                <p className={styles.productCount} aria-live="polite">
                    {!isLoading &&
                        ngettext(
                            msgid`${productCount} product`,
                            `${productCount} products`,
                            productCount,
                        )}
                </p>
                <div className={styles.headerActions}>
                    {shareUrl && (
                        <Tooltip
                            label={copied ? shareLabelSuccess : shareLabel}
                            isOpen={copied || undefined}
                        >
                            <Clickable
                                onPress={copyShareLink}
                                className={styles.headerActionButton}
                            >
                                <Svg
                                    name="link"
                                    className={styles.headerActionSvg}
                                    visuallyHiddenText={
                                        copied ? shareLabelSuccess : shareLabel
                                    }
                                />
                            </Clickable>
                        </Tooltip>
                    )}
                    {mailtoLink && (
                        <Tooltip label={emailLabel}>
                            <Clickable
                                href={mailtoLink}
                                className={styles.headerActionButton}
                            >
                                <Svg
                                    name="mail"
                                    className={styles.headerActionSvg}
                                    visuallyHiddenText={emailLabel}
                                />
                            </Clickable>
                        </Tooltip>
                    )}
                    <Tooltip label={printLabel}>
                        <Clickable
                            onPress={printPage}
                            className={styles.headerActionButton}
                        >
                            <Svg
                                name="print"
                                className={styles.headerActionSvg}
                                visuallyHiddenText={printLabel}
                            />
                        </Clickable>
                    </Tooltip>
                </div>
            </header>

            {hasError && errorMessage && (
                <div className={styles.errorState} role="alert">
                    {errorMessage}
                </div>
            )}

            {!hasError && (isLoading || (products && products.length > 0)) && (
                <ul
                    role="list"
                    className={styles.productList}
                    aria-busy={isLoading || undefined}
                >
                    {isLoading
                        ? skus.map((sku) => (
                              <ProductCard
                                  key={sku}
                                  tag="li"
                                  product={skeletonProduct(sku)}
                                  layout="vertical"
                                  skeleton
                              />
                          ))
                        : interleavePromos(
                              products ?? [],
                              gridPromos ?? [],
                          ).map((item) =>
                              item.type === "product" ? (
                                  <ProductCard
                                      key={item.product.sku}
                                      tag="li"
                                      product={item.product}
                                      layout="vertical"
                                      showFavoriteButton={showFavoriteButton}
                                  />
                              ) : (
                                  <GridPromoCard
                                      key={item.id}
                                      value={item.snippet}
                                  />
                              ),
                          )}
                </ul>
            )}

            {footerContent && (
                <div className={styles.footerContent}>
                    <RichTextStreamBlock value={footerContent} />
                </div>
            )}
        </Themed>
    );
};
